import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Send, ArrowLeft, Search, AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatternDetectorPage = () => {
  const [text, setText] = useState("");
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setAnalysis("");
    try {
      const res = await axios.post(`${API}/analyze-pattern`, { text: text.trim() });
      setAnalysis(res.data.analysis);
    } catch {
      setAnalysis("Unable to analyze right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="pattern-detector-section">
        <div className="container mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-8 text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#FFB7B2]/15 flex items-center justify-center mx-auto mb-4">
              <Search className="h-7 w-7 text-[#FFB7B2]" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2A3A] mb-3" data-testid="pattern-title">Pattern Detector</h1>
            <p className="text-sm text-[#1A2A3A]/60 max-w-lg mx-auto">
              Paste any text conversation and our AI identifies narcissistic manipulation patterns — gaslighting, DARVO, love bombing, triangulation, and more.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2" data-testid="pattern-disclaimer">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">This tool is for educational purposes only. It cannot diagnose anyone. If you are in danger, call 988 or 1-800-799-7233.</p>
          </div>

          <Card className="rounded-2xl shadow-card border border-[#E5E7EB]/50 overflow-hidden" data-testid="pattern-input-card">
            <CardContent className="p-6">
              <label className="block text-sm font-medium text-[#1A2A3A] mb-2">Paste the conversation or message</label>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm resize-none"
                placeholder="Paste the text messages, emails, or conversation you want analyzed..."
                data-testid="pattern-input"
              />
              <Button
                onClick={handleAnalyze}
                disabled={!text.trim() || loading}
                className="mt-4 bg-[#A69BE0] hover:bg-[#A69BE0]/90 text-white rounded-full px-6 py-4 font-semibold"
                data-testid="pattern-analyze-btn"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                {loading ? "Analyzing..." : "Analyze for Patterns"}
              </Button>
            </CardContent>
          </Card>

          {analysis && (
            <Card className="mt-6 rounded-2xl shadow-card border border-[#E5E7EB]/50" data-testid="pattern-results">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-4">Analysis Results</h3>
                <div className="prose prose-sm max-w-none text-[#1A2A3A]/80 whitespace-pre-wrap text-sm leading-relaxed">
                  {analysis}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

export default PatternDetectorPage;
